import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AWBDetail } from '@/services/awbService';
import { CheckCircle2, Clock, FileText, Package, Truck } from 'lucide-react';
import React, { useMemo } from 'react';

interface AWBStatsProps {
  awbs: AWBDetail[];
  totalCount: number;
}

const AWBStats: React.FC<AWBStatsProps> = ({ awbs, totalCount }) => {
  const stats = useMemo(() => {
    const delivered = awbs.filter(a => a.currentStatus === 'DELIVERED').length;
    const inTransit = awbs.filter(a => ['IN_TRANSIT', 'OUT_FOR_DELIVERY', 'PICKED_UP'].includes(a.currentStatus || '')).length;
    const pending = awbs.filter(a => a.currentStatus === 'BOOKED').length;
    const drafts = awbs.filter(a => a.currentStatus === 'DRAFT').length;
    const issues = awbs.filter(a => ['RETURNED', 'CANCELLED'].includes(a.currentStatus || '')).length;
    return { delivered, inTransit, pending, drafts, issues };
  }, [awbs]);

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
      {/* Total AWBs */}
      <Card className="hover:shadow-lg transition-all duration-200 border-l-4 border-l-blue-500">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Total AWBs</CardTitle>
          <Package className="h-5 w-5 text-blue-600" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-blue-600">{totalCount.toLocaleString()}</div>
          <p className="text-xs text-muted-foreground">All shipments</p>
        </CardContent>
      </Card>

      {/* Delivered */}
      <Card className="hover:shadow-lg transition-all duration-200 border-l-4 border-l-green-500">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Delivered</CardTitle>
          <CheckCircle2 className="h-5 w-5 text-green-600" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-green-600">{stats.delivered}</div>
          <p className="text-xs text-muted-foreground">Successfully delivered</p>
        </CardContent>
      </Card>

      {/* In Transit */}
      <Card className="hover:shadow-lg transition-all duration-200 border-l-4 border-l-orange-500">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">In Transit</CardTitle>
          <Truck className="h-5 w-5 text-orange-600" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-orange-600">{stats.inTransit}</div>
          <p className="text-xs text-muted-foreground">On the way</p>
        </CardContent>
      </Card>

      {/* Pending */}
      <Card className="hover:shadow-lg transition-all duration-200 border-l-4 border-l-purple-500">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Pending</CardTitle>
          <Clock className="h-5 w-5 text-purple-600" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-purple-600">{stats.pending}</div>
          <p className="text-xs text-muted-foreground">Booked & waiting</p>
        </CardContent>
      </Card>

      {/* Issues/Drafts */}
      <Card className="hover:shadow-lg transition-all duration-200 border-l-4 border-l-gray-500">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Drafts</CardTitle>
          <FileText className="h-5 w-5 text-gray-600" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-gray-600">{stats.drafts}</div>
          <p className="text-xs text-muted-foreground">{stats.issues > 0 ? `+${stats.issues} issues` : 'Incomplete'}</p>
        </CardContent>
      </Card>
    </div>
  );
};

export default AWBStats;
